<template>
  <div
    v-if="is_visible"
    :class="
      variant === variants.VARIANT1
        ? `modal_overlay proxima_nova ${position_classes} ${section_classes.overlay || ''}`
        : 'modal_overlay_v2 proxima_nova'
    "
    @click="handle_overlay_click"
    :id="`${source}_custom_modal`"
  >
    <transition v-if="variant === variants.VARIANT1" name="modal-fade">
      <div
        :class="`modal relative ${section_classes.container || ''}`"
        :style="{ width: modal_width, ...modal_animation_style }"
        @click.stop
        ref="modal_ref"
      >
        <div
          v-if="
            cross_variant_class_config.id ===
            custom_modal_cross_icon_variants.TOP_RIGHT_OUTSIDE
          "
          class="cross_icon_container"
          :class="cross_variant_class_config.container_class"
        >
          <ThemeImage
            :icon="imageNames.CLOSE_GREY"
            alt="close_icon"
            :class="[
              cross_variant_class_config.icon_class,
              section_classes.cross_icon,
            ]"
            @click="handle_close_click"
          />
        </div>

        <div
          v-if="visible_sections?.header"
          :class="`modal_header ${section_classes.header || ''}`"
        >
          <div class="flex flex-col">
            <h1 class="modal_title">{{ title }}</h1>
            <span
              v-if="sub_title"
              :class="`text-xs mt-1 ${section_classes.sub_title}`"
            >
              {{ sub_title }}
            </span>
          </div>

          <ThemeImage
            v-if="
              cross_variant_class_config.id ===
              custom_modal_cross_icon_variants.DEFAULT
            "
            :icon="imageNames.CLOSE_GREY"
            alt="close_icon"
            :class="[
              cross_variant_class_config.icon_class,
              section_classes.cross_icon,
            ]"
            @click="handle_close_click"
          />
        </div>

        <div
          v-if="visible_sections?.body"
          :class="`modal_body ${section_classes.body || ''}`"
        >
          <slot></slot>
        </div>
        <div
          v-if="visible_sections?.footer"
          :class="`modal_footer ${section_classes.footer || ''}`"
        >
          <slot name="footer"></slot>
        </div>
      </div>
    </transition>

    <div
      v-if="variant === variants.VARIANT2"
      class="video_modal_container_v2"
      :style="modal_animation_style"
      @click.stop
      ref="modal_ref"
    >
      <div class="video_modal_content_v2">
        <slot></slot>
        <div v-if="title" class="text_color text-2xl font-semibold">
          {{ title }}
        </div>
      </div>

      <div
        class="close_button_v2 flex flex-row gap-2 items-center cursor-pointer"
        @click="handle_close_click"
      >
        <ThemeImage
          class="size-5"
          :icon="imageNames.CLOSE_GREY"
          alt="close_icon"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, nextTick } from "vue";

import {
  variants,
  custom_modal_cross_icon_variants,
} from "@/src/constants/customComponentConstants.js";
import { imageNames } from "@/src/constants/imageConstants.js";

const props = defineProps({
  variant: {
    type: String,
    default: variants.VARIANT1,
    validator: (value) => Object.values(variants).includes(value),
  },
  source: {
    type: String,
    required: false,
    default: "",
  },
  title: {
    type: String,
    required: false,
  },
  visible: {
    type: Boolean,
    default: false,
  },
  width: {
    type: String,
    default: "400px",
  },
  sub_title: {
    type: String,
    required: false,
  },
  visible_sections: {
    type: Object,
    default: () => ({
      header: true,
      body: true,
      footer: true,
    }),
  },
  section_classes: {
    type: Object,
    default: () => ({
      overlay: "",
      container: "",
      header: "",
      body: "",
      footer: "",
      sub_title: "text_color",
      cross_icon: "",
    }),
  },
  close_on_outside_click: {
    type: Boolean,
    default: true,
  },
  cross_variant: {
    type: String,
    default: custom_modal_cross_icon_variants.DEFAULT,
    validator: (value) =>
      Object.values(custom_modal_cross_icon_variants).includes(value),
  },
  show_dividers: {
    type: Boolean,
    default: true,
  },
  position: {
    type: String,
    default: "center",
    validator: (value) => ["top", "center", "bottom"].includes(value),
  },
  click_origin: {
    type: Object,
    default: null,
  },
  forceClose: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:visible", "close"]);

const is_visible = ref(props.visible);
const modal_width = ref(props.width);
const modal_ref = ref(null);
const open_origin = ref({
  x: window.innerWidth / 2,
  y: window.innerHeight / 2,
});
const is_animating = ref(false);
const modal_animation_style = ref({});

const cross_variant_class_config = computed(() => {
  const configs = {
    [custom_modal_cross_icon_variants.DEFAULT]: {
      id: custom_modal_cross_icon_variants.DEFAULT,
      container_class: "",
      icon_class: "size-4 cursor-pointer",
    },
    [custom_modal_cross_icon_variants.TOP_RIGHT_OUTSIDE]: {
      id: custom_modal_cross_icon_variants.TOP_RIGHT_OUTSIDE,
      container_class:
        "p-1 flex items-center justify-center rounded-full cursor-pointer absolute top-[-35px] right-0",
      icon_class: "size-5",
    },
  };

  return (
    configs[props.cross_variant] ||
    configs[custom_modal_cross_icon_variants.DEFAULT]
  );
});

const position_classes = computed(() => {
  const positions = {
    top: "items-start pt-[72px]",
    center: "items-center",
    bottom: "items-end pb-[72px]",
  };

  return positions[props.position] || positions.center;
});

const animate_modal_open = async () => {
  try {
    is_animating.value = true;

    const origin_x = props.click_origin?.x || open_origin.value.x;
    const origin_y = props.click_origin?.y || open_origin.value.y;

    if (props.click_origin) {
      open_origin.value = { x: origin_x, y: origin_y };
    }

    await nextTick();

    if (!modal_ref.value) {
      modal_animation_style.value = {
        transform: "scale(0)",
        opacity: "0",
        transformOrigin: `${origin_x}px ${origin_y}px`,
      };

      await nextTick();

      setTimeout(() => {
        modal_animation_style.value = {
          transform: "scale(1)",
          opacity: "1",
          transformOrigin: `${origin_x}px ${origin_y}px`,
          transition:
            "transform 0.4s cubic-bezier(0.34, 1, 0.64, 1), opacity 0.3s ease-out",
        };
      }, 10);

      setTimeout(() => {
        is_animating.value = false;
      }, 410);
      return;
    }

    const modal_rect = modal_ref.value.getBoundingClientRect();
    const modal_center_x = modal_rect.left + modal_rect.width / 2;
    const modal_center_y = modal_rect.top + modal_rect.height / 2;

    const translate_x = origin_x - modal_center_x;
    const translate_y = origin_y - modal_center_y;

    modal_animation_style.value = {
      transform: `translate(${translate_x}px, ${translate_y}px) scale(0)`,
      opacity: "0",
      transition: "none",
    };

    await nextTick();

    setTimeout(() => {
      modal_animation_style.value = {
        transform: "translate(0, 0) scale(1)",
        opacity: "1",
        transition:
          "transform 0.4s cubic-bezier(0.34, 1, 0.64, 1), opacity 0.3s ease-out",
      };
    }, 10);

    setTimeout(() => {
      is_animating.value = false;
    }, 410);
  } catch (error) {
    console.error("An error occurred while animating model open: ", error);
  }
};

const animate_modal_close = async () => {
  try {
    if (!modal_ref.value || is_animating.value) return;

    is_animating.value = true;

    const modal_rect = modal_ref.value.getBoundingClientRect();
    const modal_center_x = modal_rect.left + modal_rect.width / 2;
    const modal_center_y = modal_rect.top + modal_rect.height / 2;

    const translate_x = open_origin.value.x - modal_center_x;
    const translate_y = open_origin.value.y - modal_center_y;

    modal_animation_style.value = {
      transform: `translate(${translate_x}px, ${translate_y}px) scale(0)`,
      opacity: "0",
      transition:
        "transform 0.35s cubic-bezier(0.36, 0, 0.66, 0), opacity 0.25s ease-in",
    };

    setTimeout(() => {
      is_visible.value = false;
      emit("close");
      emit("update:visible", false);
      modal_animation_style.value = {};
      is_animating.value = false;
    }, 350);
  } catch (error) {
    console.error("An error occurred while animating modal close: ", error);
  }
};

watch(
  () => props.visible,
  async (new_val) => {
    if (new_val) {
      const last_click = document.last_click_event;
      if (last_click && Date.now() - last_click.timestamp < 100) {
        open_origin.value = { x: last_click.x, y: last_click.y };
      }

      is_visible.value = true;
      await nextTick();
      await animate_modal_open();
    } else {
      if (!is_animating.value) {
        await animate_modal_close();
      }
    }
  },
);

watch(
  () => props.width,
  (new_val) => {
    modal_width.value = new_val;
  },
);

// Simple force close watcher - bypasses animation blocking
watch(
  () => props.forceClose,
  (newValue) => {
    if (newValue) {
      is_visible.value = false;
      emit("update:visible", false);
      emit("close");
    }
  },
);

if (typeof window !== "undefined") {
  document.addEventListener(
    "click",
    (event) => {
      document.last_click_event = {
        x: event.clientX,
        y: event.clientY,
        timestamp: Date.now(),
      };
    },
    true,
  );
}

const handle_overlay_click = (event) => {
  if (!props.close_on_outside_click) return;
  animate_modal_close();
};

const handle_close_click = (event) => {
  animate_modal_close();
};

const close_modal = (is_outside_click = false) => {
  if (is_outside_click && !props.close_on_outside_click) return;

  is_visible.value = false;
  emit("close");
  emit("update:visible", false);
};
</script>

<style scoped>
.modal_overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: var(--custom-modal-bg);
  border-radius: 8px;
  max-width: 90%;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  color: var(--text-color);
  transform-origin: center center;
}

.modal_header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: v-bind(
    'show_dividers ? "1px solid var(--white-opacity-8)" : "none"'
  );
  padding: 16px;
}

.modal_title {
  color: var(--foreground-rgb);
  font-family: "Proxima Nova", sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}

.modal_body {
  padding: 24px 16px;
  gap: 24px;
}

.modal_footer {
  display: flex;
  justify-content: flex-end;
  border-top: v-bind(
    'show_dividers ? "1px solid var(--white-opacity-8)" : "none"'
  );
  padding: 16px;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition:
    opacity 0.3s ease-out,
    transform 0.3s ease-out;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.cross_icon_container {
  background: var(--custom-modal-bg);
}

.modal_overlay_v2 {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.video_modal_container_v2 {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  background: var(--background-color);
  border-radius: 12px;
  overflow: visible;
  transform-origin: center center;
}

.video_modal_content_v2 {
  display: flex;
  padding: 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
}

.close_button_v2 {
  position: absolute;
  top: -40px;
  right: 0;
  background: var(--background-color);
  border: none;
  border-radius: 50%;
  display: flex;
  padding: 4px;
  align-items: center;
}
</style>
